import { Tabs } from '@base-ui/react/tabs';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { type ChartToken, SUMMARY_CONFIG, THEME_CONFIG, THEME_ORDER, type TileConfig } from '../model/themes.config';
import type { ThemeSummary } from '../model/types';
import type { AwpTheme } from '../useAwpDashboardParams';

export type HealthThemeTabsState = 'loading' | 'error' | 'empty' | 'ready';

export interface HealthThemeTabsProps {
  /** The four themes' counts, from `useThemeSummaries`. Read only when `state` is `ready`. */
  summaries?: ThemeSummary[];
  /** The Health Summary tile's count. Read only when `state` is `ready`. */
  outOfRangeTotal?: number;
  activeTheme: AwpTheme;
  onSelect: (theme: AwpTheme) => void;
  /** Who's reading: the Summary tile says "out of range" to a member and "abnormal" to a physician. */
  audience: 'member' | 'physician';
  state: HealthThemeTabsState;
  onRetry: () => void;
  /** The panel the tiles control: a `Tabs.Panel`, which must sit inside the strip's `Tabs.Root`. */
  children?: ReactNode;
  className?: string;
}

/**
 * Sets the tile's accent as a custom property, so the tint, edge, bar and glyph classes below are written
 * once. Keyed by token rather than theme so `THEME_CONFIG` stays the one place a theme's colour is chosen;
 * the strings are literal because Tailwind only generates classes it can find in the source.
 */
const ACCENT: Record<ChartToken, string> = {
  '--chart-1': '[--theme-accent:var(--chart-1)]',
  '--chart-2': '[--theme-accent:var(--chart-2)]',
  '--chart-3': '[--theme-accent:var(--chart-3)]',
  '--chart-4': '[--theme-accent:var(--chart-4)]',
  '--chart-5': '[--theme-accent:var(--chart-5)]',
};

interface Tile extends TileConfig {
  value: AwpTheme;
  summary?: ThemeSummary;
}

/** The Health Summary first, then the four themes in the design's order. */
function tiles(summaries: ThemeSummary[] | undefined): Tile[] {
  return [
    { ...SUMMARY_CONFIG, value: 'summary' },
    ...THEME_ORDER.map((code) => ({
      ...THEME_CONFIG[code],
      value: THEME_CONFIG[code].slug,
      summary: summaries?.find((summary) => summary.themeCode === code),
    })),
  ];
}

// Two-up on a phone with the Summary taking the full row, three at @xl, five across at @3xl.
const GRID = 'grid grid-cols-2 gap-4 @xl/themes:grid-cols-3 @3xl/themes:grid-cols-5';
const TILE_BOX = 'flex min-h-32 flex-col justify-between gap-6 rounded-2xl border p-4 @xl/themes:gap-8 @xl/themes:p-6';
const SUMMARY_SPAN = 'col-span-2 @xl/themes:col-span-1';

/**
 * The AWP health-theme strip: the Health Summary and the four themes as tiles that are also the tabs
 * picking what the dashboard below shows. Presentational: the counts arrive computed by the data layer,
 * and selection is the caller's (the URL, on the dashboards).
 *
 * Loading draws skeleton tiles at the real tiles' measurements, so nothing shifts when counts arrive, and
 * error is an Alert with Retry. Neither renders the tab list, so neither renders `children`: a panel with
 * no tabs controlling it would be announced as a tab panel for nothing. Empty shows the tiles without counts.
 */
export function HealthThemeTabs({
  summaries,
  outOfRangeTotal,
  activeTheme,
  onSelect,
  audience,
  state,
  onRetry,
  children,
  className,
}: HealthThemeTabsProps) {
  const { t } = useTranslation('shared');

  if (state === 'error') {
    return (
      <div className={cn('@container/themes', className)}>
        <Alert variant="destructive">
          <Icon name="alertTriangle" weight="solid" />
          <AlertTitle>{t('awpDashboard.strip.error.title')}</AlertTitle>
          <AlertDescription>
            <p>{t('awpDashboard.strip.error.body')}</p>
            <Button variant="outline" size="sm" className="mt-3 min-h-11" onClick={onRetry}>
              <Icon name="refreshCw" size="xs" />
              {t('awpDashboard.strip.error.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div className={cn('@container/themes', className)}>
        {/* Outside the busy grid: assistive tech may hold back announcements from inside an aria-busy region. */}
        <span className="sr-only" role="status">
          {t('awpDashboard.strip.loading')}
        </span>
        <div className={GRID} aria-busy="true">
          {tiles(undefined).map((tile, i) => (
            <div key={tile.value} className={cn(TILE_BOX, 'bg-card', i === 0 && SUMMARY_SPAN)}>
              <Skeleton className="h-3.5 w-24" />
              <div className="flex items-center justify-between gap-2">
                <Skeleton className="size-8 rounded-md" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const ready = state === 'ready';
  const summaryTerm = audience === 'member' ? 'awpDashboard.strip.outOfRange' : 'awpDashboard.strip.abnormal';

  return (
    <Tabs.Root
      value={activeTheme}
      onValueChange={(value: AwpTheme) => onSelect(value)}
      className={cn('@container/themes', className)}
    >
      {/* Arrow keys move and select at once, so the panel follows the focused tile. */}
      <Tabs.List activateOnFocus aria-label={t('awpDashboard.strip.label')} className={GRID}>
        {tiles(summaries).map((tile, i) => (
          <Tabs.Tab
            key={tile.value}
            value={tile.value}
            className={cn(
              TILE_BOX,
              ACCENT[tile.chartToken],
              'group relative text-left shadow-sm transition-[background-color,border-color] duration-fast ease-out',
              // The hover lift is a pseudo-element fading in, not an animated box-shadow, which would
              // repaint the tile every frame instead of compositing.
              'after:pointer-events-none after:absolute after:inset-0 after:rounded-2xl after:opacity-0 after:shadow-md after:transition-opacity after:duration-fast after:ease-out after:content-[""] hover:after:opacity-100',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'border-border bg-card not-data-active:hover:bg-[color-mix(in_oklab,var(--theme-accent)_8%,var(--card))]',
              'data-active:border-[color-mix(in_oklab,var(--theme-accent)_40%,var(--card))] data-active:bg-[color-mix(in_oklab,var(--theme-accent)_8%,var(--card))] data-active:shadow-md',
              i === 0 && SUMMARY_SPAN,
            )}
          >
            {/* The accent bar is the only thing that needs the tile's rounded clip, so it gets its own
                clipper: on the tile itself, overflow-hidden would also clip the hover lift's shadow. */}
            <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl" aria-hidden="true">
              <span className="absolute inset-y-0 left-0 w-1 bg-(--theme-accent)" />
            </span>
            {/* `relative` so the content paints above the positioned clipper. */}
            <span className="relative text-base font-semibold text-foreground">{t(tile.i18nKey)}</span>
            <span className="relative flex flex-wrap items-end justify-between gap-4 @xl/themes:gap-2">
              <Icon name={tile.icon} size="3xl" className="text-(--theme-accent)" />
              {ready &&
                (tile.value === 'summary' ? (
                  <Count>{t(summaryTerm, { count: outOfRangeTotal ?? 0 })}</Count>
                ) : (
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Count>{t('awpDashboard.strip.flagged', { count: tile.summary?.flagged ?? 0 })}</Count>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {t('awpDashboard.strip.labs', { count: tile.summary?.total ?? 0 })}
                    </span>
                  </span>
                ))}
            </span>
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {children}
    </Tabs.Root>
  );
}

/** A count led by a dot in the tile's accent. The dot is decoration: the count is always carried as text. */
function Count({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-foreground">
      <span
        aria-hidden="true"
        className="grid size-3.5 shrink-0 place-items-center rounded-full bg-[color-mix(in_oklab,var(--theme-accent)_24%,var(--card))]"
      >
        <span className="size-2 rounded-full bg-(--theme-accent)" />
      </span>
      <span>{children}</span>
    </span>
  );
}
